import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  Heart,
  AlertTriangle,
  Pin,
  Trash2,
  BarChart3,
  Shield,
  Send,
  TrendingUp,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useSocialOverview, useRecentPosts } from '../../hooks/useSocialAnalytics';
import { useTogglePinPost, useHidePost, useCreatePost } from '../../hooks/usePostManagement';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useAuth } from '../../constants/providers/AuthProvider';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

// Note: ConfirmDialog uses onCancel, not onClose

export default function SocialDashboard() {
  const { data: stats, isLoading: statsLoading } = useSocialOverview();
  const { data: recentPosts, isLoading: postsLoading } = useRecentPosts(15);
  const togglePin = useTogglePinPost();
  const hidePost = useHidePost();
  const createPost = useCreatePost();
  const { company } = useCurrentCompany();
  const { user } = useAuth();

  const [newPostContent, setNewPostContent] = useState('');
  const [deletePostId, setDeletePostId] = useState<string | null>(null);

  const handleCreatePost = async () => {
    if (!newPostContent.trim() || !company?.id || !user?.id) return;
    try {
      await createPost.mutateAsync({
        content: newPostContent.trim(),
        companyId: company.id,
        userId: user.id,
      });
      setNewPostContent('');
    } catch (err) {
      console.error('Failed to create post:', err);
    }
  };

  const handleDeletePost = async () => {
    if (!deletePostId) return;
    try {
      await hidePost.mutateAsync({ postId: deletePostId });
      setDeletePostId(null);
    } catch (err) {
      console.error('Failed to hide post:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center">
            <MessageSquare size={20} className="text-text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#EBFFEE]">Social Dashboard</h1>
            <p className="text-sm text-[#A9A3A3]">Manage your community and track engagement</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/social/moderation"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#363232] border border-[#504A4A] hover:border-[#9B1421] text-[#EBFFEE] text-sm transition-colors"
          >
            <Shield className="w-4 h-4" />
            Moderation
          </Link>
          <Link
            to="/social/analytics"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#363232] border border-[#504A4A] hover:border-[#9B1421] text-[#EBFFEE] text-sm transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Total Posts', value: stats?.totalPosts ?? '-', icon: MessageSquare, color: 'text-blue-400' },
          { label: 'Comments', value: stats?.totalComments ?? '-', icon: MessageSquare, color: 'text-green-400' },
          { label: 'Reactions', value: stats?.totalReactions ?? '-', icon: Heart, color: 'text-pink-400' },
          { label: 'Active Posters', value: stats?.activePosters ?? '-', icon: Users, color: 'text-purple-400' },
          {
            label: 'Pending Reports',
            value: stats?.pendingReports ?? '-',
            icon: AlertTriangle,
            color: stats?.pendingReports ? 'text-yellow-400' : 'text-green-400',
          },
        ].map((stat) => (
          <Card key={stat.label} className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="!p-4">
              <div className="flex items-center gap-3">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold text-[#EBFFEE]">
                    {statsLoading ? '...' : stat.value}
                  </p>
                  <p className="text-xs text-[#A9A3A3]">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create Post + Recent Posts (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Create Post */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] text-sm flex items-center gap-2">
                <Send className="w-4 h-4 text-rollops-accent" />
                Create Announcement
              </CardTitle>
            </CardHeader>
            <CardContent className="!p-4">
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Share an update with your community..."
                className="w-full px-3 py-2 rounded-lg bg-[#363232] border border-[#504A4A] text-[#EBFFEE] placeholder-[#706A6A] focus:outline-none focus:border-[#9B1421] transition-colors resize-none min-h-[80px]"
                maxLength={2000}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-[#706A6A]">
                  {newPostContent.length}/2000
                </span>
                <Button
                  onClick={handleCreatePost}
                  disabled={!newPostContent.trim() || createPost.isPending}
                  className="bg-[#9B1421] hover:bg-[#E8535E] text-white px-4 py-1.5 text-sm rounded-lg disabled:opacity-50"
                >
                  {createPost.isPending ? 'Posting...' : 'Post'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Posts */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-rollops-accent" />
                Recent Posts
              </CardTitle>
            </CardHeader>
            <CardContent className="!p-0">
              {postsLoading ? (
                <div className="p-6 text-center text-[#A9A3A3]">Loading posts...</div>
              ) : !recentPosts?.length ? (
                <div className="p-6 text-center text-[#A9A3A3]">
                  No posts yet. Create one above to get your community started!
                </div>
              ) : (
                <div className="divide-y divide-[#504A4A]">
                  {recentPosts.map((post) => (
                    <div key={post.id} className="p-4 hover:bg-[#363232]/30 transition-colors">
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full bg-[#363232] flex items-center justify-center overflow-hidden flex-shrink-0">
                          {post.author?.avatar_url ? (
                            <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Users className="w-4 h-4 text-[#706A6A]" />
                          )}
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-[#EBFFEE]">
                              {post.author?.first_name} {post.author?.last_name}
                            </span>
                            {post.is_pinned && (
                              <Pin className="w-3 h-3 text-[#E8535E]" />
                            )}
                            <span className="text-xs text-[#706A6A]">
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-[#A9A3A3] line-clamp-2">{post.content}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-[#706A6A]">
                            <span>{post.comments_count || 0} comments</span>
                          </div>
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => togglePin.mutate({ postId: post.id, isPinned: post.is_pinned })}
                            className={`p-1.5 rounded-lg transition-colors ${
                              post.is_pinned
                                ? 'text-[#E8535E] hover:bg-[#9B1421]/20'
                                : 'text-[#706A6A] hover:bg-[#363232] hover:text-[#A9A3A3]'
                            }`}
                            title={post.is_pinned ? 'Unpin' : 'Pin'}
                            aria-label={post.is_pinned ? 'Unpin post' : 'Pin post'}
                          >
                            <Pin className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletePostId(post.id)}
                            className="p-1.5 rounded-lg text-[#706A6A] hover:bg-[#363232] hover:text-red-400 transition-colors"
                            title="Delete post"
                            aria-label="Delete post"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Quick Links */}
        <div className="space-y-6">
          {/* Pending Reports Alert */}
          {stats?.pendingReports ? (
            <Card className="bg-[#221F1F] border-yellow-600/50">
              <CardContent className="!p-4">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm font-medium text-yellow-400">
                    {stats.pendingReports} Pending Report{stats.pendingReports > 1 ? 's' : ''}
                  </span>
                </div>
                <Link
                  to="/social/moderation"
                  className="block w-full text-center px-3 py-2 rounded-lg bg-yellow-600/20 text-yellow-400 text-sm hover:bg-yellow-600/30 transition-colors"
                >
                  Review Reports
                </Link>
              </CardContent>
            </Card>
          ) : null}

          {/* Community Tips */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] text-sm">Community Building</CardTitle>
            </CardHeader>
            <CardContent className="!p-4 space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#9B1421] mt-1.5 flex-shrink-0" />
                <p className="text-xs text-[#A9A3A3]">
                  Regular posts keep members engaged and increase class attendance
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#9B1421] mt-1.5 flex-shrink-0" />
                <p className="text-xs text-[#A9A3A3]">
                  Pin important announcements so new members see them first
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#9B1421] mt-1.5 flex-shrink-0" />
                <p className="text-xs text-[#A9A3A3]">
                  Celebrate student achievements to build a positive culture
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#9B1421] mt-1.5 flex-shrink-0" />
                <p className="text-xs text-[#A9A3A3]">
                  Active communities see 2-3x better member retention
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletePostId}
        onCancel={() => setDeletePostId(null)}
        onConfirm={handleDeletePost}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
      />
    </div>
  );
}
